/**
 * [KA] Auto-hide — sitewide empty-content hider. Consolidates the former per-template scripts:
 *   - "Scripts [KA] Hide accordions"      (collection + product templates)
 *   - "Scripts [KA] Hide sections"        (product template)
 *   - "Scripts [KA] Hide collection list" (collection templates)
 *   - "[KA] Hide empty blocks"            (footer group, was disabled)
 *   - 'Skjul "Se alt.."'                  (header group, mega menu)
 *
 * Configured via data-attributes on [data-ka-auto-hide]
 * (see sections/ka-auto-hide.liquid). Generic empty-content checks are
 * scoped to #MainContent; header/footer behaviors have their own toggles.
 */
(function () {
  const configEl = document.querySelector('[data-ka-auto-hide]');
  if (!configEl) return;

  const cfg = {
    accordions: configEl.dataset.accordions === 'true',
    sections: configEl.dataset.sections === 'true',
    emptyBlocks: configEl.dataset.emptyBlocks === 'true',
    collectionLists: configEl.dataset.collectionLists === 'true',
    collectionListSelector: configEl.dataset.collectionListSelector || '.ui-test-collection-list',
    footerBlocks: configEl.dataset.footerBlocks === 'true',
    seeAll: configEl.dataset.seeAll === 'true',
    seeAllPrefixes: (configEl.dataset.seeAllPrefixes || 'se alt,se alle')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  };

  function hide(el) {
    if (el) el.style.setProperty('display', 'none', 'important');
  }

  function cleanText(s) {
    return (s || '')
      .replace(/ /g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/Læs mere|Read more|Se mere|Åbn|Open|Close|Luk/gi, '')
      .trim();
  }

  function hasMedia(el) {
    return !!(
      el.querySelector('img[src], source[srcset]') ||
      el.querySelector('iframe[src]') ||
      el.querySelector('video[src], video source[src]')
    );
  }

  // Strip nodes that never count as real content — used by BOTH block and
  // section checks. Operates on a clone, never the live DOM.
  function stripNoise(clone) {
    // Descendants we already hid this run — otherwise their textContent still
    // counts and stops ancestors from cascading empty.
    clone.querySelectorAll('[data-ka-hidden-empty="1"]').forEach((n) => n.remove());

    // Accessibility-only labels (e.g. the "Afspil video" text inside a play
    // icon). Visible to screen readers, but not real page content.
    clone.querySelectorAll('.visually-hidden, .sr-only, .visually-hidden\\@lap-and-up').forEach((n) => n.remove());

    // Video blocks with NO assigned video render only a placeholder wrapper
    // (Horizon: .video-placeholder-wrapper + inline placeholder SVG, no real
    // media). Treat those as non-content. Wrappers that DO contain a real
    // video/image/iframe are left intact so genuine videos still count.
    clone.querySelectorAll('.video-placeholder-wrapper').forEach((wrapper) => {
      const hasReal = wrapper.querySelector('video[src], video source[src], img[src], iframe[src]');
      if (!hasReal) wrapper.remove();
    });
  }

  /* ── Empty accordion items (main content) ─────────────────────────── */

  function getAccordionContentEl(item) {
    return (
      item.querySelector('.accordion__content') ||
      item.querySelector('.accordion-content') ||
      item.querySelector('[data-accordion-content]') ||
      item.querySelector('[data-content]') ||
      null
    );
  }

  function isAccordionItemEmpty(item) {
    if (item.tagName && item.tagName.toLowerCase() === 'details') {
      const clone = item.cloneNode(true);
      const summary = clone.querySelector('summary');
      if (summary) summary.remove();
      stripNoise(clone);
      if (hasMedia(clone)) return false;
      return cleanText(clone.textContent).length === 0;
    }
    const contentEl = getAccordionContentEl(item);
    if (contentEl) {
      const clone = contentEl.cloneNode(true);
      stripNoise(clone);
      if (hasMedia(clone)) return false;
      return cleanText(clone.textContent).length === 0;
    }
    const tmp = item.cloneNode(true);
    tmp.querySelectorAll('h1,h2,h3,h4,h5,h6,button,summary').forEach((n) => n.remove());
    stripNoise(tmp);
    if (hasMedia(tmp)) return false;
    return cleanText(tmp.textContent).length === 0;
  }

  /* Sections that build their content with JavaScript (Typeform, Elfsight,
     any custom-liquid embed) are empty at DOM-ready by design — never treat
     them as empty. */
  function isExemptSection(section) {
    return !!section.querySelector(
      '.custom-liquid, [data-tf-live], [class*="elfsight"], [id*="typeform"]'
    );
  }

  function isSectionEffectivelyEmpty(section) {
    if (isExemptSection(section)) return false;
    const clone = section.cloneNode(true);
    stripNoise(clone);
    // Section rule: a lone heading (with no body text or media) counts as empty.
    clone.querySelectorAll('h1,h2,h3,h4,h5,h6,button,summary').forEach((n) => n.remove());
    if (hasMedia(clone)) return false;
    return cleanText(clone.textContent).length === 0;
  }

  /* ── Empty group blocks within sections ────────────────────────────── */

  // Block rule is stricter than section rule: a block is only empty when it has
  // no text (headings INCLUDED — a heading is legit content for a single block)
  // and no real media, after noise (a11y labels, unset video placeholders) is
  // stripped.
  function isBlockEffectivelyEmpty(block) {
    if (isExemptSection(block)) return false;
    const clone = block.cloneNode(true);
    stripNoise(clone);
    if (hasMedia(clone)) return false;
    return cleanText(clone.textContent).length === 0;
  }

  function processEmptyBlocks() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    // Reverse document order = leaf-first. Nested empty blocks are hidden
    // before their ancestors are evaluated, so a parent whose only content
    // was an empty child block still cascades to hidden in this same pass.
    const blocks = Array.from(main.querySelectorAll('.group-block')).reverse();

    blocks.forEach((block) => {
      if (block.dataset.kaHiddenEmpty === '1') return;

      // Opt-out hook: any element (or ancestor) marked data-ka-keep is left alone.
      // Used by e.g. blocks/ka-typeform-embed.liquid where the iframe injects async.
      if (block.closest('[data-ka-keep]')) return;

      if (isBlockEffectivelyEmpty(block)) {
        hide(block);
        block.dataset.kaHiddenEmpty = '1';
      }
    });
  }

  function processMainSections() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const sections = main.querySelectorAll('.shopify-section, .section');

    sections.forEach((section) => {
      if (section.dataset.kaHiddenEmpty === '1') return;
      if (section.closest('[data-ka-keep]')) return;

      const items = section.querySelectorAll(
        'details, .accordion-item, .accordion__item, [data-accordion-item], accordion-custom'
      );

      if (items.length) {
        if (!cfg.accordions) return;
        items.forEach((item) => {
          if (item.closest('[data-ka-keep]')) return;
          if (isAccordionItemEmpty(item)) hide(item);
        });
        const stillVisible = Array.from(items).some((i) => {
          const cs = getComputedStyle(i);
          return cs.display !== 'none' && cs.visibility !== 'hidden';
        });
        if (!stillVisible) {
          hide(section);
          section.dataset.kaHiddenEmpty = '1';
        }
        return;
      }

      if (cfg.sections) {
        // Comparison slider with only placeholder art
        const slider = section.querySelector('comparison-slider-component');
        if (slider) {
          const placeholder = slider.querySelector('.comparison-slider__placeholder svg');
          if (placeholder && !hasMedia(slider)) {
            hide(section);
            section.dataset.kaHiddenEmpty = '1';
          }
          return;
        }

        // A section DESIGNED with a video slot (even if the video is unset and
        // only a placeholder shows) is an intentional media layout, not a
        // heading-only metafield section. Skip the aggressive heading-only
        // hide — the empty video block itself is handled by processEmptyBlocks.
        if (section.querySelector('.video-placeholder-wrapper')) return;

        if (isSectionEffectivelyEmpty(section)) {
          hide(section);
          section.dataset.kaHiddenEmpty = '1';
        }
      }
    });
  }

  /* ── Empty collection lists ────────────────────────────────────────── */

  function processCollectionLists() {
    document.querySelectorAll(cfg.collectionListSelector).forEach((section) => {
      const items = section.querySelectorAll('.collection-card');

      if (!items.length) {
        hide(section);
        return;
      }

      let hasRealCollection = false;
      items.forEach((card) => {
        const isPlaceholder = card.hasAttribute('data-placeholder');
        const link = card.querySelector('.collection-card__link');
        const hasHref = link && link.getAttribute('href');
        if (!isPlaceholder && hasHref) hasRealCollection = true;
      });

      if (!hasRealCollection) hide(section);
    });
  }

  /* ── Empty footer accordion blocks ─────────────────────────────────── */

  function processFooterBlocks() {
    const footer = document.querySelector('#footer-group, .footer, footer');
    if (!footer) return;

    footer.querySelectorAll('accordion-custom > details.details').forEach((details) => {
      const content = details.querySelector(':scope > .details-content');
      if (!content) return;

      const useful = !!content.querySelector(
        'img, video, iframe, table, ul, ol, li, a[href], button, input, textarea, select, svg, picture, source'
      );
      const text = cleanText(content.textContent);
      if (!useful && text.length === 0) hide(details);
    });
  }

  /* ── Mega-menu "Se alt.." links (desktop) ──────────────────────────── */

  function isSeeAll(text) {
    const t = (text || '').trim().toLowerCase();
    return cfg.seeAllPrefixes.some((p) => t.startsWith(p));
  }

  function seeAllHide(el) {
    if (!el) return;
    el.dataset.kaHideSeeAll = '1';
    el.style.setProperty('display', 'none', 'important');
  }

  function seeAllShow(el) {
    if (!el) return;
    if (el.dataset.kaHideSeeAll === '1') {
      delete el.dataset.kaHideSeeAll;
      el.style.removeProperty('display');
    }
  }

  // Runs against ALL mega-menu panels in the DOM, open or closed. The updated
  // Horizon no longer exposes open panels via aria-expanded/aria-controls on
  // .menu-list__link, so open-state detection broke. Hiding links inside
  // closed panels is harmless, and this stays immune to future changes in how
  // the theme triggers its menus. Panels injected later are caught by the
  // MutationObserver re-running run().
  function applySeeAll(root) {
    if (!root) return;

    root.querySelectorAll('.mega-menu__list > .mega-menu__column').forEach((col) => {
      col.querySelectorAll(':scope > div > a.mega-menu__link > .wrap-text').forEach((span) => {
        const div = span.closest('div');
        if (isSeeAll(span.textContent)) seeAllHide(div);
        else seeAllShow(div);
      });

      col.querySelectorAll('ul li a.mega-menu__link > .wrap-text').forEach((span) => {
        const li = span.closest('li');
        if (isSeeAll(span.textContent)) seeAllHide(li);
        else seeAllShow(li);
      });

      const hasVisibleContent = Array.from(col.querySelectorAll('a.mega-menu__link')).some((a) => {
        const container = a.closest('li') || a.closest('div') || a;
        return !container.dataset.kaHideSeeAll;
      });

      if (!hasVisibleContent) seeAllHide(col);
      else seeAllShow(col);
    });
  }

  /* ── Orchestration ─────────────────────────────────────────────────── */

  function run() {
    // Blocks first, so section-level checks below see the already-hidden
    // descendants (via stripNoise) and can cascade correctly.
    if (cfg.emptyBlocks) processEmptyBlocks();
    if (cfg.accordions || cfg.sections) processMainSections();
    if (cfg.collectionLists) processCollectionLists();
    if (cfg.footerBlocks) processFooterBlocks();
    if (cfg.seeAll) applySeeAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Debounced MutationObserver — rAF-coalesced so our own hides don't loop
  let scheduled = false;
  const mo = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();